import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { getElogi, createElogio, deleteElogio, generaTestoElogio, syncToWix, downloadPDFElogio, getAziende } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, ScrollText, Sparkles, ExternalLink, Trash2, Send, Loader2, FileText, QrCode, Eye, Building2 } from 'lucide-react';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';

const DEMO_ELOGIO = {
  nome: 'Mario',
  cognome: 'Rossi',
  data_nascita: '1945-03-15',
  data_morte: '2024-01-10',
  biografia_raw: 'Mario era un uomo generoso e dedito alla famiglia. Ha lavorato come artigiano per oltre 40 anni, tramandando la sua passione ai figli. Era conosciuto nel quartiere per la sua disponibilità ad aiutare chiunque avesse bisogno.',
  passioni_raw: 'Amava la pesca, il giardinaggio e passare le domeniche con i nipoti. Era un appassionato di musica classica e non perdeva mai un concerto in piazza.',
  email: 'demo@esempio.it',
};

const ElogiPage = () => {
  const { isAdmin, isAzienda } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [elogi, setElogi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [aziendaFilter, setAziendaFilter] = useState('all');
  const [aziende, setAziende] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [elogioToDelete, setElogioToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [creatingWithAI, setCreatingWithAI] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [createdElogioId, setCreatedElogioId] = useState(null);
  const [createdElogioName, setCreatedElogioName] = useState('');
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    data_nascita: '',
    data_morte: '',
    biografia_raw: '',
    passioni_raw: '',
    email: '',
  });

  useEffect(() => {
    loadElogi();
    if (isAdmin()) {
      getAziende()
        .then((r) => setAziende(r.data || []))
        .catch(() => {});
    }
    
    // Controlla se è stato richiesto un elogio demo
    if (searchParams.get('demo') === 'true') {
      setFormData(DEMO_ELOGIO);
      setDialogOpen(true);
      toast.info('Dati demo pre-compilati. Puoi modificarli o creare direttamente l\'elogio.');
      // Rimuovi il parametro dall'URL
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const loadElogi = async () => {
    try {
      const response = await getElogi();
      setElogi(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento elogi');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (elogio) => {
    setDownloadingId(elogio.id);
    try {
      await downloadPDFElogio(elogio.id, elogio.nome, elogio.cognome);
      toast.success('PDF scaricato');
    } catch (error) {
      toast.error('Errore nel download del PDF');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreatingWithAI(true);
    try {
      // 1. Crea l'elogio
      const response = await createElogio(formData);
      const newElogioId = response.data.id;
      
      toast.success('Elogio creato, generazione testo AI in corso...');
      
      // 2. Genera automaticamente il testo con AI
      try {
        await generaTestoElogio(newElogioId);
        toast.success('Testo generato con AI!');
        
        // 3. Mostra popup di successo
        setCreatedElogioId(newElogioId);
        setCreatedElogioName(`${formData.nome} ${formData.cognome}`);
        setSuccessDialogOpen(true);
      } catch (aiError) {
        toast.error('Elogio creato ma errore nella generazione AI');
      }
      
      setDialogOpen(false);
      setFormData({ nome: '', cognome: '', data_nascita: '', data_morte: '', biografia_raw: '', passioni_raw: '', email: '' });
      loadElogi();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setCreatingWithAI(false);
    }
  };

  const handleViewElogio = () => {
    setSuccessDialogOpen(false);
    navigate(`/elogi/${createdElogioId}`);
  };

  const handleDownloadCreatedPDF = async () => {
    if (!createdElogioId) return;
    setDownloadingId(createdElogioId);
    try {
      const nameParts = createdElogioName.split(' ');
      await downloadPDFElogio(createdElogioId, nameParts[0] || '', nameParts.slice(1).join(' ') || '');
      toast.success('PDF scaricato');
    } catch (error) {
      toast.error('Errore nel download del PDF');
    } finally {
      setDownloadingId(null);
      setSuccessDialogOpen(false);
    }
  };

  const openDeleteDialog = (elogio) => {
    setElogioToDelete(elogio);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!elogioToDelete) return;
    setDeleting(true);
    try {
      await deleteElogio(elogioToDelete.id);
      toast.success('Elogio eliminato');
      loadElogi();
      setDeleteDialogOpen(false);
      setElogioToDelete(null);
    } catch (error) {
      toast.error('Errore eliminazione');
    } finally {
      setDeleting(false);
    }
  };

  const handleGeneraAI = async (id) => {
    setGeneratingId(id);
    try {
      await generaTestoElogio(id);
      toast.success('Testo generato con AI');
      loadElogi();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore generazione AI');
    } finally {
      setGeneratingId(null);
    }
  };

  const handleSyncWix = async (id) => {
    setSyncingId(id);
    try {
      await syncToWix(id);
      toast.success('Sincronizzato online');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore sincronizzazione');
    } finally {
      setSyncingId(null);
    }
  };

  const filteredElogi = elogi.filter(e => {
    const searchLower = search.toLowerCase();
    const matchesSearch = !search ||
      e.nome?.toLowerCase().includes(searchLower) ||
      e.cognome?.toLowerCase().includes(searchLower) ||
      e.id?.toLowerCase().includes(searchLower) ||
      e.nome_azienda?.toLowerCase().includes(searchLower);
    
    let matchesAzienda = true;
    if (aziendaFilter !== 'all') {
      if (aziendaFilter === '__none__') {
        matchesAzienda = !e.origine;
      } else {
        matchesAzienda = e.origine === aziendaFilter;
      }
    }
    
    return matchesSearch && matchesAzienda;
  });

  const getStatusBadge = (elogio) => {
    if (!elogio.qr_id) {
      return <Badge variant="secondary">Senza QR</Badge>;
    }
    if (elogio.qr_attivo) {
      return <Badge className="bg-green-100 text-green-700">Attivo</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-700">QR Assegnato</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="elogi-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 lg:mb-8">
        <div>
          <h1 
            className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-900 tracking-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Elogi
          </h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Gestione memorial e commemorazioni</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" data-testid="new-elogio-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Elogio
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>Nuovo Elogio</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nome">Nome *</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({...formData, nome: e.target.value})}
                    required
                    data-testid="input-nome"
                  />
                </div>
                <div>
                  <Label htmlFor="cognome">Cognome *</Label>
                  <Input
                    id="cognome"
                    value={formData.cognome}
                    onChange={(e) => setFormData({...formData, cognome: e.target.value})}
                    required
                    data-testid="input-cognome"
                  />
                </div>
                <div>
                  <Label htmlFor="data_nascita">Data Nascita</Label>
                  <Input
                    id="data_nascita"
                    type="date"
                    value={formData.data_nascita}
                    onChange={(e) => setFormData({...formData, data_nascita: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="data_morte">Data Decesso</Label>
                  <Input
                    id="data_morte"
                    type="date"
                    value={formData.data_morte}
                    onChange={(e) => setFormData({...formData, data_morte: e.target.value})}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="email">Email contatto</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="biografia_raw">Biografia</Label>
                  <Textarea
                    id="biografia_raw"
                    rows={3}
                    placeholder="Racconta la storia di questa persona..."
                    value={formData.biografia_raw}
                    onChange={(e) => setFormData({...formData, biografia_raw: e.target.value})}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="passioni_raw">Passioni e interessi</Label>
                  <Textarea
                    id="passioni_raw"
                    rows={2}
                    placeholder="Hobby, interessi, passioni..."
                    value={formData.passioni_raw}
                    onChange={(e) => setFormData({...formData, passioni_raw: e.target.value})}
                  />
                </div>
              </div>
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="w-full sm:w-auto" disabled={creatingWithAI}>
                  Annulla
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" data-testid="submit-elogio-btn" disabled={creatingWithAI}>
                  {creatingWithAI ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creazione in corso...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Crea Elogio con AI
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog Successo Creazione */}
        <Dialog open={successDialogOpen} onOpenChange={setSuccessDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-600" style={{ fontFamily: 'Playfair Display, serif' }}>
                <Sparkles className="h-5 w-5" />
                Elogio Creato con Successo!
              </DialogTitle>
              <DialogDescription>
                L'elogio per <strong>{createdElogioName}</strong> è stato creato e i testi sono stati generati automaticamente dall'AI.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex flex-col sm:flex-row gap-2 mt-4">
              <Button 
                variant="outline" 
                onClick={handleDownloadCreatedPDF}
                disabled={downloadingId === createdElogioId}
                className="w-full sm:w-auto"
              >
                {downloadingId === createdElogioId ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4 mr-2" />
                )}
                Scarica PDF
              </Button>
              <Button 
                onClick={handleViewElogio}
                className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto"
              >
                <Eye className="h-4 w-4 mr-2" />
                Vedi Elogio
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search & Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-3">
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Cerca per nome, cognome, ID o agenzia..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-elogi"
          />
        </div>
        {isAdmin() && (
          <div className="w-full sm:w-72">
            <Select value={aziendaFilter} onValueChange={setAziendaFilter}>
              <SelectTrigger data-testid="filter-azienda-select">
                <SelectValue placeholder="Filtra per agenzia" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le agenzie</SelectItem>
                <SelectItem value="__none__">Senza agenzia</SelectItem>
                {aziende.map((a) => (
                  <SelectItem key={a.id_azienda} value={a.id_azienda}>
                    {a.ragione_sociale}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {isAdmin() && aziendaFilter !== 'all' && (
          <Button 
            variant="ghost"
            onClick={() => setAziendaFilter('all')}
            className="text-slate-600"
            data-testid="clear-filter-btn"
          >
            Reset filtro
          </Button>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4">
        {filteredElogi.map((elogio) => (
          <Card key={elogio.id} data-testid={`elogio-card-${elogio.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-slate-100">
                    <ScrollText className="h-5 w-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{elogio.nome} {elogio.cognome}</p>
                    <p className="text-xs text-slate-500 font-mono">{elogio.id}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {elogio.is_demo && (
                    <Badge className="bg-purple-100 text-purple-700">DEMO</Badge>
                  )}
                  {getStatusBadge(elogio)}
                </div>
              </div>

              <div className="flex items-center gap-4 text-sm mb-4">
                {elogio.qr_id && (
                  <div className="flex items-center gap-1">
                    <QrCode className="h-4 w-4 text-slate-400" />
                    <span className="font-mono">#{elogio.qr_id}</span>
                  </div>
                )}
                {elogio.processed === 'YES' ? (
                  <Badge className="bg-green-100 text-green-700">Elaborato</Badge>
                ) : (
                  <Badge variant="secondary">Da elaborare</Badge>
                )}
              </div>

              {isAdmin() && elogio.nome_azienda && (
                <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                  <Building2 className="h-4 w-4 text-slate-400" />
                  <span className="truncate">{elogio.nome_azienda}</span>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                {elogio.processed !== 'YES' && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleGeneraAI(elogio.id)}
                    disabled={generatingId === elogio.id}
                    className="flex-1"
                  >
                    {generatingId === elogio.id ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <Sparkles className="h-4 w-4 mr-1" />
                    )}
                    AI
                  </Button>
                )}
                
                {isAdmin() && elogio.processed === 'YES' && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleSyncWix(elogio.id)}
                    disabled={syncingId === elogio.id}
                  >
                    {syncingId === elogio.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                )}

                {elogio.processed === 'YES' && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleDownloadPDF(elogio)}
                    disabled={downloadingId === elogio.id}
                  >
                    {downloadingId === elogio.id ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <FileText className="h-4 w-4 mr-1" />
                    )}
                    PDF
                  </Button>
                )}

                {elogio.link && elogio.qr_attivo && (
                  <a href={elogio.link} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm">
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Link
                    </Button>
                  </a>
                )}

                {/* Bottone elimina: sempre visibile per admin, per altri solo se non attivo */}
                {(isAdmin || !elogio.qr_attivo) && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    className={`${elogio.wix_synced ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50' : 'text-red-600 hover:text-red-700 hover:bg-red-50'}`}
                    onClick={() => openDeleteDialog(elogio)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}

                <Link to={`/elogi/${elogio.id}`} className="flex-1">
                  <Button variant="outline" size="sm" className="w-full">
                    Gestisci
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ))}
        {filteredElogi.length === 0 && (
          <div className="p-8 text-center text-slate-500">
            Nessun elogio trovato
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <Card className="hidden lg:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="elogi-table">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 font-medium text-slate-600">ID</th>
                  <th className="text-left p-4 font-medium text-slate-600">Nome</th>
                  {isAdmin() && (
                    <th className="text-left p-4 font-medium text-slate-600">Agenzia</th>
                  )}
                  <th className="text-center p-4 font-medium text-slate-600">QR</th>
                  <th className="text-center p-4 font-medium text-slate-600">Elaborato</th>
                  <th className="text-center p-4 font-medium text-slate-600">Stato</th>
                  <th className="text-right p-4 font-medium text-slate-600">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredElogi.map((elogio) => (
                  <tr 
                    key={elogio.id} 
                    className="border-b hover:bg-slate-50 transition-colors"
                    data-testid={`elogio-row-${elogio.id}`}
                  >
                    <td className="p-4">
                      <span className="text-sm font-mono text-slate-500">{elogio.id}</span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-slate-100">
                          <ScrollText className="h-4 w-4 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{elogio.nome} {elogio.cognome}</p>
                          {elogio.email && <p className="text-xs text-slate-500">{elogio.email}</p>}
                        </div>
                      </div>
                    </td>
                    {isAdmin() && (
                      <td className="p-4" data-testid={`elogio-azienda-${elogio.id}`}>
                        {elogio.nome_azienda ? (
                          <div className="flex items-center gap-2 text-sm text-slate-700">
                            <Building2 className="h-4 w-4 text-slate-400 flex-shrink-0" />
                            <span className="truncate max-w-[180px]" title={elogio.nome_azienda}>
                              {elogio.nome_azienda}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 italic">—</span>
                        )}
                      </td>
                    )}
                    <td className="p-4 text-center">
                      {elogio.qr_id ? (
                        <Badge variant="outline" className="font-mono">#{elogio.qr_id}</Badge>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      {elogio.processed === 'YES' ? (
                        <Badge className="bg-green-100 text-green-700">SI</Badge>
                      ) : (
                        <Badge variant="secondary">NO</Badge>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {elogio.is_demo && (
                          <Badge className="bg-purple-100 text-purple-700 text-xs">DEMO</Badge>
                        )}
                        {getStatusBadge(elogio)}
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {elogio.processed !== 'YES' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleGeneraAI(elogio.id)}
                            disabled={generatingId === elogio.id}
                            data-testid={`genera-ai-${elogio.id}`}
                          >
                            {generatingId === elogio.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Sparkles className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                        
                        {isAdmin() && elogio.processed === 'YES' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleSyncWix(elogio.id)}
                            disabled={syncingId === elogio.id}
                            data-testid={`sync-wix-${elogio.id}`}
                          >
                            {syncingId === elogio.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Send className="h-4 w-4" />
                            )}
                          </Button>
                        )}

                        {elogio.processed === 'YES' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDownloadPDF(elogio)}
                            disabled={downloadingId === elogio.id}
                            data-testid={`download-pdf-${elogio.id}`}
                          >
                            {downloadingId === elogio.id ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                              <FileText className="h-4 w-4 mr-1" />
                            )}
                            PDF
                          </Button>
                        )}

                        {elogio.link && elogio.qr_attivo && (
                          <a href={elogio.link} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" size="sm">
                              <ExternalLink className="h-4 w-4 mr-1" />
                              Link
                            </Button>
                          </a>
                        )}

                        {/* Bottone elimina: sempre visibile per admin, per altri solo se non attivo */}
                        {(isAdmin || !elogio.qr_attivo) && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            className={`${elogio.wix_synced ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50' : 'text-red-600 hover:text-red-700 hover:bg-red-50'}`}
                            onClick={() => openDeleteDialog(elogio)}
                            data-testid={`delete-${elogio.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}

                        <Link to={`/elogi/${elogio.id}`}>
                          <Button variant="outline" size="sm" data-testid={`gestisci-elogio-${elogio.id}`}>
                            Gestisci
                          </Button>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredElogi.length === 0 && (
            <div className="p-8 text-center text-slate-500">
              Nessun elogio trovato
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Conferma Eliminazione */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        title={elogioToDelete?.wix_synced ? "⚠️ Elimina Elogio PUBBLICATO" : "Elimina Elogio"}
        description={
          elogioToDelete?.wix_synced 
            ? "ATTENZIONE: Questo elogio è già stato PUBBLICATO e PAGATO. L'eliminazione rimuoverà l'elogio dal sistema ma NON dal sito web pubblico. Dovrai richiedere la rimozione manuale. Questa azione è IRREVERSIBILE."
            : "Eliminando questo elogio, tutti i dati associati (immagini, testi generati) saranno persi definitivamente."
        }
        itemName={elogioToDelete ? `${elogioToDelete.nome} ${elogioToDelete.cognome}` : ''}
        loading={deleting}
      />
    </div>
  );
};

export default ElogiPage;
